import styled from 'styled-components';

export const StyledLanguageSwitcher = styled.div`
  position: relative;
  display: inline-block;
`;

export const StyledLanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  min-height: 36px;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    border-color: ${({ theme }) => theme.colors.borderHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  .language-text {
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
    
    @media ${({ theme }) => theme.media.maxSm} {
      .language-name {
        display: none;
      }
      
      .language-flag {
        font-size: 1.2em;
      }
    }
  }

  @media ${({ theme }) => theme.media.touch} {
    min-height: 44px;
    min-width: 44px;
  }
`;

export const StyledLanguageDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing.xs});
  right: 0;
  min-width: 200px;
  max-width: 280px;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: ${({ theme }) => theme.spacing.xs};
  z-index: 9999; /* Very high z-index to ensure it's above everything */
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-8px')});
  transition: all 0.15s ease-out;
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  
  /* Ensure dropdown doesn't get cut off on the right */
  @media (max-width: 1024px) {
    right: 0;
    left: auto;
    transform-origin: top right;
  }

  /* For mobile navigation - position dropdown more carefully */
  @media ${({ theme }) => theme.media.mobile} {
    position: fixed;
    top: auto;
    bottom: 80px;
    right: ${({ theme }) => theme.spacing.md};
    left: auto;
    transform: none;
    min-width: 220px;
    max-width: calc(100vw - 2 * ${({ theme }) => theme.spacing.md});
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media ${({ theme }) => theme.media.maxSm} {
    right: ${({ theme }) => theme.spacing.sm};
    min-width: 200px;
    max-width: calc(100vw - 2 * ${({ theme }) => theme.spacing.sm});
  }
`;

export const StyledLanguageOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  min-height: 48px;

  &:hover, &:focus-visible {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    outline: none;
  }

  &.active {
    background: ${({ theme }) => theme.colors.primary}15;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }

  .flag {
    font-size: 1.2em;
    flex-shrink: 0;
  }

  .language-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  .native-name {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .english-name {
    font-size: ${({ theme }) => theme.fontSizes.xs};
    color: ${({ theme }) => theme.colors.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media ${({ theme }) => theme.media.touch} {
    min-height: 44px;
  }

  @media ${({ theme }) => theme.media.reducedMotion} {
    transition: none;
  }
`;
